import { useId, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@ridereserv/shared/utils";
import type { Audience as PageAudience } from "./AudienceValueSection";

/**
 * Which audience an answer is most relevant to. Drives the per-item relevance
 * tag and which questions show under the page-level audience. `"both"` items
 * always show for every audience.
 */
type ItemAudience = "driver" | "company" | "both";

interface FaqItem {
  /** The objection, phrased as the visitor would ask it. */
  question: string;
  /** Factual, platform-accurate answer copy. */
  answer: string;
  /** Primary audience this answer speaks to. */
  audience: ItemAudience;
}

/**
 * The FAQ knowledge base. Every answer is factual to the platform and derived
 * from the canonical tier fixture (`packages/database/src/tier-fixture.ts`) and
 * the product docs — no fabricated stats, prices, or claims.
 */
const FAQ_ITEMS: readonly FaqItem[] = [
  {
    question: "Do my customers need to download an app to book?",
    answer:
      "No. Customers book straight from your branded booking page in any browser — no app, no account required. Operators on Business Plus and up can also embed the same booking flow as a widget directly on their own website.",
    audience: "company",
  },
  {
    question: "Can a driver work with more than one operator?",
    answer:
      "Yes. Driver documents are portable: a driver uploads their license, insurance, and other paperwork once, then shares them with any operator they work for. Switching or adding operators never means re-uploading the same documents.",
    audience: "driver",
  },
  {
    question: "Is there a free plan?",
    answer:
      "Yes. The Free plan costs nothing and covers basic reservation management for a solo operator. When you outgrow it, Professional unlocks analytics, calendar sync, and online payments — and business plans scale up from there.",
    audience: "driver",
  },
  {
    question: "How do online payments work?",
    answer:
      "Paid tiers connect their own Stripe account through Stripe Connect, so customers can pay for rides online and funds settle directly to you. Every plan — including Free — can still track payment status manually for cash or offline trips.",
    audience: "both",
  },
  {
    question: "Can I embed booking on my own website?",
    answer:
      "Yes. The embeddable booking widget is available on Business Plus and Business Ultimate. Drop it into your existing site so customers book without ever leaving your domain, all under your own branding.",
    audience: "company",
  },
  {
    question: "Do you support flight tracking for airport pickups?",
    answer:
      "Flight tracking is built into Business Ultimate at no extra cost. On Paid Driver, Business, and Business Plus it's available as an add-on, so airport operations stay covered whether you're a solo driver or a full fleet.",
    audience: "both",
  },
  {
    question: "What is AI auto-dispatch?",
    answer:
      "Auto-dispatch is a Business Ultimate capability that suggests the best driver assignment for incoming reservations, so your team coordinates a busy board without assigning every trip by hand. Admins and managers stay in control of the final call.",
    audience: "company",
  },
  {
    question: "Can I manage multiple locations from one account?",
    answer:
      "Yes. Multi-location management is included in Business Ultimate, alongside unlimited drivers, audit logs, and a custom subdomain — built for larger fleets running more than one base of operations.",
    audience: "company",
  },
] as const;

/**
 * Per-audience relevance tag styling. Neutral metadata chips (not competing
 * accents): the page-level audience scope recolors genuine `primary-*` accents
 * to amber for drivers, so these relevance tags stay neutral to read as quiet
 * labels rather than fighting the active accent. Flip-safe.
 */
const AUDIENCE_TAG: Record<ItemAudience, { label: string; className: string }> = {
  driver: {
    label: "Drivers",
    className: "bg-neutral-100 text-neutral-600",
  },
  company: {
    label: "Operators",
    className: "bg-neutral-100 text-neutral-600",
  },
  both: {
    label: "Everyone",
    className: "bg-neutral-100 text-neutral-600",
  },
};

/**
 * Map the page-level audience (`companies` / `drivers`) to the FAQ item audience
 * key (`company` / `driver`). A page audience always shows its own questions
 * plus the `both` ("Everyone") ones.
 */
const PAGE_TO_ITEM: Record<PageAudience, ItemAudience> = {
  companies: "company",
  drivers: "driver",
};

interface FaqRowProps {
  item: FaqItem;
  isOpen: boolean;
  onToggle: () => void;
  /** Stable id prefix so the button/region aria wiring is unique. */
  baseId: string;
}

/**
 * A single accordion row: a full-width trigger button wired to a collapsible
 * answer region. The trigger owns `aria-expanded` and `aria-controls`; the
 * region is `role="region"` labelled by the trigger. The answer collapses via a
 * `grid-rows` height transition (motion-safe) so it animates without measuring
 * the DOM. When closed it is marked `inert` + `aria-hidden`, which keeps its
 * content out of the tab order, in-page find, and the accessibility tree while
 * still allowing the open/close transition to play. (The HTML `hidden`
 * attribute is deliberately not used here: its only effect is the UA
 * `display:none` rule, which an author-origin `display:grid` utility overrides,
 * so `hidden` would be silently inert AND would defeat the height transition.)
 * @param root0 - Row props
 * @param root0.item - The question/answer/audience data
 * @param root0.isOpen - Whether this row is expanded
 * @param root0.onToggle - Toggles the row open/closed
 * @param root0.baseId - Stable id prefix for aria wiring
 * @returns The accordion row
 */
function FaqRow({ item, isOpen, onToggle, baseId }: FaqRowProps) {
  const buttonId = `${baseId}-trigger`;
  const regionId = `${baseId}-region`;
  const tag = AUDIENCE_TAG[item.audience];

  return (
    <div className="group">
      <h3 className="m-0">
        <button
          type="button"
          id={buttonId}
          aria-expanded={isOpen}
          aria-controls={regionId}
          onClick={onToggle}
          className={cn(
            "flex min-h-11 w-full items-center gap-3 py-4 text-left",
            "rounded-sm transition-colors",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
            "hover:text-primary-700"
          )}
        >
          <span className="flex-1 text-base font-semibold text-foreground">
            {item.question}
          </span>
          <span
            className={cn(
              "hidden shrink-0 rounded-full px-2 py-0.5 text-xs font-medium sm:inline-block",
              tag.className
            )}
          >
            {tag.label}
          </span>
          <ChevronDown
            aria-hidden="true"
            strokeWidth={1.75}
            className={cn(
              "h-3.5 w-3.5 shrink-0 text-neutral-400 transition-transform duration-200 ease-out motion-reduce:transition-none",
              "group-hover:text-primary-600",
              isOpen && "rotate-180"
            )}
          />
        </button>
      </h3>
      <div
        id={regionId}
        role="region"
        aria-labelledby={buttonId}
        aria-hidden={!isOpen}
        inert={!isOpen}
        className={cn(
          "grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none",
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        )}
      >
        <div className="overflow-hidden">
          <p className="max-w-prose pb-5 pr-7 text-sm leading-relaxed text-neutral-600">
            {item.answer}
          </p>
        </div>
      </div>
    </div>
  );
}

interface FaqSectionProps {
  /**
   * The page-level audience whose questions to show (plus the "Everyone" ones).
   * Controlled by the page toggle so the FAQ matches the value + pricing above.
   */
  audience: PageAudience;
}

/**
 * Homepage FAQ section.
 *
 * An accessible, self-contained accordion that answers the most common
 * objections. Which questions appear is driven by the page-level audience
 * toggle: a company reader sees the company questions plus the "Everyone" ones,
 * a driver reader sees the driver questions plus "Everyone". There is no
 * separate per-section filter — the page toggle is the single control.
 *
 * Editorial two-column layout (sticky intro rail + accordion list) on large
 * screens, single column on mobile. Accordion open-state is local React state.
 * All copy is static and factual to the platform. Token classes only and
 * flip-safe for dark mode.
 * @param root0 - Component props
 * @param root0.audience - The page-level audience whose questions to show
 * @returns The FAQ section
 */
export function FaqSection({ audience }: FaqSectionProps) {
  const baseId = useId();
  const [openId, setOpenId] = useState<string | null>(null);
  const itemAudience = PAGE_TO_ITEM[audience];

  // Stable per-item ids (index-based) so aria wiring and open-state tracking
  // survive audience changes without colliding.
  const items = useMemo(
    () =>
      FAQ_ITEMS.map((item, index) => ({
        ...item,
        id: `${baseId}-faq-${String(index)}`,
      })),
    [baseId]
  );

  const visible = items.filter(
    (item) => item.audience === itemAudience || item.audience === "both"
  );

  return (
    <section
      aria-labelledby={`${baseId}-heading`}
      className="border-t border-neutral-200 bg-background"
    >
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:grid lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] lg:gap-16 lg:py-20 lg:px-8">
        {/* Intro rail — sticky on large screens so it anchors the list. */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <span className="inline-flex items-center gap-1.5 font-mono text-xs font-medium uppercase tracking-[0.2em] text-primary-600">
            <span aria-hidden="true" className="h-px w-6 bg-primary-600" />
            FAQ
          </span>
          <h2
            id={`${baseId}-heading`}
            className="mt-4 text-3xl font-bold tracking-tight text-foreground sm:text-4xl"
          >
            Questions, answered
          </h2>
          <p className="mt-4 text-base leading-relaxed text-neutral-600">
            Whether you drive solo or dispatch a fleet, here&rsquo;s how
            RideReserv works — straight answers, no fine print.
          </p>
        </div>

        {/* Accordion list — hairline dividers, flat chrome. */}
        <div className="mt-10 lg:mt-0">
          <ul className="divide-y divide-neutral-200 border-y border-neutral-200">
            {visible.map((item) => (
              <li key={item.id}>
                <FaqRow
                  item={item}
                  baseId={item.id}
                  isOpen={openId === item.id}
                  onToggle={() => {
                    setOpenId((current) =>
                      current === item.id ? null : item.id
                    );
                  }}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
